import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog, DialogTitle, DialogContent, IconButton, Stepper, Step, StepLabel, Box, Button
} from '@mui/material';
import { Close as CloseIcon, ArrowBack, ArrowForward } from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import MailingListForm from '@components/mailinglist/MailingListForm';
import MailingListUpload from '@components/mailinglist/MailingListUpload';
import MailingListPreview from '@components/mailinglist/MailingListPreview';
import MailingListImport from '@components/mailinglist/MailingListImport';


function MailingListCreationDialog({ modalCreateOpen, setModalCreateOpen, onImportDone, reloadDialog }) {
    const { t, i18n } = useTranslation();

    const { isSuperAdmin, getCurrentCustomerId } = useAuth();
    const [activeStep, setActiveStep] = useState(0);
    const [dialogHeight, setDialogHeight] = useState(540);
    const [nextDisabled, setNextDisabled] = useState(true);
    const form = useRef();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        customer_id: '',
        lang: null,
    });
    const [file, setFile] = useState(null);
    const [encoding, setEncoding] = useState(null);
    const [columns, setColumns] = useState([]);
    const steps = useMemo(() => [
        {
            key: 'upload',
            label: t('Upload file'),
            width: '600px',
            height: '480px',
        },
        {
            key: 'preview',
            label: t('Preview'),
            width: '90vw',
            height: '90vh',
        },
        {
            key: 'information',
            label: t('Information'),
            width: '600px',
            height: '492px',
        },
        {
            key: 'import',
            label: t('Import'),
            width: '600px',
            height: '356px',
        },
    ], [t]);

    const reset = useCallback(() => {
        setActiveStep(0);
        setFormData({
            name: '',
            description: '',
            customer_id: '',
            lang: null,
        });
        setFile(null);
        setColumns([]);
    }, []);

    useEffect(() => {
        // Nom construit à partir de "file.name" en retirant l'extension 
        // et en remplaçant les caractères spéciaux par des espaces
        // + première lettre en majuscule
        const name = file ?
            file.name.replace(/\.[^/.]+$/, '')
                .replace(/[^a-zA-Z0-9]/g, ' ')
                .trim()
                .toLowerCase()
                .replace(/^\w/g, l => l.toUpperCase())
            : '';
        setFormData({
            name: name,
            description: '',
            customer_id: isSuperAdmin() ? '' : getCurrentCustomerId(),
            lang: i18n.language,
        });
    }, [setFormData, t, i18n.language, isSuperAdmin, getCurrentCustomerId, file]);

    useEffect(() => {
        reset();
    }, [reset, reloadDialog]);

    useEffect(() => {
        setDialogHeight(steps[activeStep].height);
    }, [activeStep, steps, setDialogHeight]);

    const handleClose = (event, reason) => {
        if (reason && reason === 'backdropClick') {
            return; // Ne pas fermer si clic sur le fond
        }
        setModalCreateOpen(false);
        reset();
    }

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setNextDisabled(true);
    }

    const handlePrevious = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
        setNextDisabled(true);
    }

    const handleSubmitInformation = (data) => {
        setFormData(data);
        handleNext();
    }

    const handleSubmitUpload = (data) => {
        handleNext();
    }

    const handleSubmitPreview = (data) => {
        handleNext();
    }

    return (
        <Dialog
            open={modalCreateOpen}
            onClose={handleClose}
            aria-labelledby="parent-modal-title"
            fullWidth
            maxWidth={false}
            sx={{
                '& .MuiDialog-paper': {
                    width: steps[activeStep].width,
                    height: dialogHeight,
                    transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
                },
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {t('Create mailing list')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent sx={{ p: 0, width: '100%' }}>

                <Box mx={1} mb={5}>
                    <Stepper activeStep={activeStep}>
                        {steps.map((step, index) => {
                            const stepProps = {};
                            const labelProps = {};
                            return (
                                <Step key={step.key} {...stepProps}>
                                    <StepLabel {...labelProps}>{step.label}</StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                </Box>

                <Box m={2}>

                    {activeStep === 0 &&
                        <MailingListUpload
                            setNextDisabled={setNextDisabled}
                            file={file}
                            setFile={setFile}
                            setColumns={setColumns}
                            setEncoding={setEncoding}
                            buttons={
                                <Box display="flex" justifyContent="space-between">
                                    <Button variant="outlined" onClick={handleClose}>
                                        {t('Cancel')}
                                    </Button>
                                    <Button type="submit" variant="contained" color="primary" endIcon={<ArrowForward />}
                                        disabled={nextDisabled} onClick={handleSubmitUpload}>
                                        {t('Next')}
                                    </Button>
                                </Box>
                            } />
                    }

                    {activeStep === 1 &&
                        <MailingListPreview
                            file={file}
                            encoding={encoding}
                            setNextDisabled={setNextDisabled}
                            columns={columns}
                            setColumns={setColumns}
                            buttons={
                                <Box display="flex" justifyContent="space-between">
                                    <Button variant="outlined" startIcon={<ArrowBack />} onClick={handlePrevious}>
                                        {t('Previous')}
                                    </Button>
                                    <Button type="submit" variant="contained" color="primary" endIcon={<ArrowForward />} disabled={nextDisabled} onClick={handleSubmitPreview}>
                                        {t('Next')}
                                    </Button>
                                </Box>
                            } />
                    }

                    {activeStep === 2 &&
                        <MailingListForm onSubmit={handleSubmitInformation} ref={form} formData={formData}
                            setNextDisabled={setNextDisabled} fieldsVariant="outlined"
                            buttons={
                                <Box display="flex" justifyContent="space-between">
                                    <Button variant="outlined" startIcon={<ArrowBack />} onClick={handlePrevious}>
                                        {t('Previous')}
                                    </Button>
                                    <Button type="submit" variant="contained" color="primary" endIcon={<ArrowForward />} disabled={nextDisabled}>
                                        {t('Start import')}
                                    </Button>
                                </Box>
                            }
                        />
                    }

                    {activeStep === 3 &&
                        <MailingListImport
                            formData={formData}
                            file={file}
                            encoding={encoding}
                            setNextDisabled={setNextDisabled}
                            setDialogHeight={setDialogHeight}
                            columns={columns}
                            setColumns={setColumns}
                            onImportDone={onImportDone}
                            buttons={
                                <Box display="flex" justifyContent="space-between">
                                    <Button variant="outlined" startIcon={<ArrowBack />} disabled>
                                        {t('Previous')}
                                    </Button>
                                    <Button variant="contained" color="primary" disabled={nextDisabled} onClick={handleClose}>
                                        {t('Finish')}
                                    </Button>
                                </Box>
                            } />
                    }

                </Box>

            </DialogContent>
        </ Dialog>
    );

}

export default MailingListCreationDialog;